import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import FilterModal from "../Filter Modal/FilterModal";

export default function DataTable_Component({ apidata, columns, searchBy }) {
  const [dataFilter, setDataFilter] = useState("");

  const [filteredData, setFilteredData] = useState([]);

  const [showFilterModal, setShowFilterModal] = useState(false);

  // -----------------------------------------
  // APPLIED FILTERS
  // -----------------------------------------

  const [filters, setFilters] = useState({
    serviceType: "",
    company: "",
    status: "",
    vehicleStatus: "",
    nextServiceMonth: "",
    nextServiceYear: "",
  });

  // -----------------------------------------
  // FILTER TABLE DATA
  // -----------------------------------------

  useEffect(() => {
    let filtered = [...apidata];

    // -----------------------------------------
    // SEARCH BY VEHICLE
    // -----------------------------------------

    if (dataFilter) {
      const searchValue = dataFilter.toLowerCase();

      filtered = filtered.filter((item) => {
        if (searchBy === "vehicle") {
          const numberplate = item.vehicle?.numberplate || "";

          return numberplate.toLowerCase().includes(searchValue);
        }

        return false;
      });
    }

    // -----------------------------------------
    // SERVICE TYPE FILTER
    // -----------------------------------------

    if (filters.serviceType) {
      filtered = filtered.filter(
        (item) => item.serviceType === filters.serviceType,
      );
    }

    // -----------------------------------------
    // VEHICLE STATUS FILTER
    // -----------------------------------------

    if (filters.vehicleStatus) {
      filtered = filtered.filter(
        (item) => item.vehicle?.status === filters.vehicleStatus,
      );
    }

    // -----------------------------------------
    // COMPANY FILTER
    // -----------------------------------------

    if (filters.company) {
      filtered = filtered.filter(
        (item) => item.vehicle?.id_type?.company === filters.company,
      );
    }

    // -----------------------------------------
    // STATUS FILTER
    // -----------------------------------------

    if (filters.status) {
      filtered = filtered.filter((item) => item.status === filters.status);
    }

    // -----------------------------------------
    // NEXT SERVICE MONTH + YEAR FILTER
    // -----------------------------------------

    if (filters.nextServiceMonth !== "" || filters.nextServiceYear !== "") {
      filtered = filtered.filter((item) => {
        if (!item.nextServiceDueDate) {
          return false;
        }

        const serviceDate = new Date(item.nextServiceDueDate);

        const serviceMonth = serviceDate.getMonth();
        const serviceYear = serviceDate.getFullYear();

        // Month
        const monthMatches =
          filters.nextServiceMonth === "" ||
          serviceMonth === Number(filters.nextServiceMonth);

        // Year
        const yearMatches =
          filters.nextServiceYear === "" ||
          serviceYear === Number(filters.nextServiceYear);

        return monthMatches && yearMatches;
      });
    }

    setFilteredData(filtered);
  }, [dataFilter, apidata, searchBy, filters]);

  // -----------------------------------------
  // STATUS COUNTS
  //
  // Status filter is intentionally NOT applied
  // here. This keeps the counts meaningful when
  // Pending/Completed is selected.
  // -----------------------------------------

  const statusCountData = useMemo(() => {
    let data = [...apidata];

    // -----------------------------------------
    // SEARCH
    // -----------------------------------------

    if (dataFilter) {
      const searchValue = dataFilter.toLowerCase();

      data = data.filter((item) => {
        if (searchBy === "vehicle") {
          const numberplate = item.vehicle?.numberplate || "";

          return numberplate.toLowerCase().includes(searchValue);
        }

        return false;
      });
    }

    // -----------------------------------------
    // SERVICE TYPE
    // -----------------------------------------

    if (filters.serviceType) {
      data = data.filter((item) => item.serviceType === filters.serviceType);
    }

    // -----------------------------------------
    // COMPANY
    // -----------------------------------------

    if (filters.company) {
      data = data.filter(
        (item) => item.vehicle?.id_type?.company === filters.company,
      );
    }

    // -----------------------------------------
    // VEHICLE STATUS
    // -----------------------------------------

    if (filters.vehicleStatus) {
      data = data.filter(
        (item) => item.vehicle?.status === filters.vehicleStatus,
      );
    }

    // -----------------------------------------
    // NEXT SERVICE MONTH + YEAR
    // -----------------------------------------

    if (filters.nextServiceMonth !== "" || filters.nextServiceYear !== "") {
      data = data.filter((item) => {
        if (!item.nextServiceDueDate) {
          return false;
        }

        const serviceDate = new Date(item.nextServiceDueDate);

        const serviceMonth = serviceDate.getMonth();
        const serviceYear = serviceDate.getFullYear();

        const monthMatches =
          filters.nextServiceMonth === "" ||
          serviceMonth === Number(filters.nextServiceMonth);

        const yearMatches =
          filters.nextServiceYear === "" ||
          serviceYear === Number(filters.nextServiceYear);

        return monthMatches && yearMatches;
      });
    }

    return {
      total: data.length,

      pending: data.filter((item) => item.status === "Pending").length,

      completed: data.filter((item) => item.status === "Completed").length,
    };
  }, [
    apidata,
    dataFilter,
    searchBy,
    filters.serviceType,
    filters.vehicleStatus,
    filters.company,
    filters.nextServiceMonth,
    filters.nextServiceYear,
  ]);

  // -----------------------------------------
  // STATUS BUTTON CLICK
  // -----------------------------------------

  const handleStatusFilter = (status) => {
    setFilters((prev) => ({
      ...prev,

      // If same status is clicked again,
      // remove the status filter.
      status: prev.status === status ? "" : status,
    }));
  };

  // -----------------------------------------
  // APPLY FILTERS
  // -----------------------------------------

  const applyFilters = (newFilters) => {
    setFilters(newFilters);
    setShowFilterModal(false);
  };

  // -----------------------------------------
  // REMOVE ALL FILTERS
  // -----------------------------------------

  const removeFilters = () => {
    setFilters({
      serviceType: "",
      company: "",
      status: "",
      vehicleStatus: "",
      nextServiceMonth: "",
      nextServiceYear: "",
    });

    setShowFilterModal(false);
  };

  return (
    <div className="table-responsive">
      {/* ----------------------------------------- */}
      {/* FILTER MODAL */}
      {/* ----------------------------------------- */}

      <FilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        applyFilters={applyFilters}
        removeFilters={removeFilters}
        apidata={apidata}
        selectedFilters={filters}
      />

      {/* ----------------------------------------- */}
      {/* SEARCH + FILTER + STATUS */}
      {/* ----------------------------------------- */}

      <div className="row justify-content-between align-items-center mb-3">
        {/* ----------------------------------------- */}
        {/* SEARCH */}
        {/* ----------------------------------------- */}

        <div className="col-md-3 mb-2 mb-md-0">
          <input
            type="text"
            placeholder={`Search by ${searchBy}`}
            className="form-control"
            value={dataFilter}
            onChange={(e) => setDataFilter(e.target.value)}
          />
        </div>

        {/* ----------------------------------------- */}
        {/* FILTER + STATUS BUTTONS */}
        {/* ----------------------------------------- */}

        <div className="col-md-9 mb-2 mb-md-0">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {/* ----------------------------------------- */}
            {/* FILTER BUTTON */}
            {/* ----------------------------------------- */}

            <button
              className="btn btn-primary"
              onClick={() => setShowFilterModal(true)}
            >
              Filter
            </button>

            {/* ----------------------------------------- */}
            {/* TOTAL */}
            {/* ----------------------------------------- */}

            <button
              type="button"
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  status: "",
                }))
              }
              className="btn d-flex align-items-center"
              style={{
                backgroundColor: filters.status === "" ? "#e9ecef" : "#f8f9fa",

                border:
                  filters.status === ""
                    ? "2px solid #6c757d"
                    : "1px solid #ced4da",

                borderRadius: "7px",
                padding: "8px 14px",
                color: "#212529",
                fontWeight: "500",
              }}
            >
              <span
                style={{
                  width: "7px",
                  height: "7px",
                  backgroundColor: "#6c757d",
                  borderRadius: "50%",
                  display: "inline-block",
                  marginRight: "8px",
                }}
              />
              Total: {statusCountData.total}
            </button>

            {/* ----------------------------------------- */}
            {/* PENDING */}
            {/* ----------------------------------------- */}

            <button
              type="button"
              onClick={() => handleStatusFilter("Pending")}
              className="btn d-flex align-items-center"
              style={{
                backgroundColor:
                  filters.status === "Pending" ? "#fff3cd" : "#fff8e1",

                border:
                  filters.status === "Pending"
                    ? "2px solid #ffc107"
                    : "1px solid #ffe08a",

                borderRadius: "7px",
                padding: "8px 14px",
                color: "#212529",
                fontWeight: "500",
              }}
            >
              <span
                style={{
                  width: "7px",
                  height: "7px",
                  backgroundColor: "#ffc107",
                  borderRadius: "50%",
                  display: "inline-block",
                  marginRight: "8px",
                }}
              />
              Pending: {statusCountData.pending}
            </button>

            {/* ----------------------------------------- */}
            {/* COMPLETED */}
            {/* ----------------------------------------- */}

            <button
              type="button"
              onClick={() => handleStatusFilter("Completed")}
              className="btn d-flex align-items-center"
              style={{
                backgroundColor:
                  filters.status === "Completed" ? "#d1e7dd" : "#e2f3eb",

                border:
                  filters.status === "Completed"
                    ? "2px solid #198754"
                    : "1px solid #a8d5c0",

                borderRadius: "7px",
                padding: "8px 14px",
                color: "#212529",
                fontWeight: "500",
              }}
            >
              <span
                style={{
                  width: "7px",
                  height: "7px",
                  backgroundColor: "#198754",
                  borderRadius: "50%",
                  display: "inline-block",
                  marginRight: "8px",
                }}
              />
              Completed: {statusCountData.completed}
            </button>
          </div>
        </div>
      </div>

      {/* ----------------------------------------- */}
      {/* TABLE */}
      {/* ----------------------------------------- */}

      {filteredData.length > 0 ? (
        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          fixedHeader
          highlightOnHover
          responsive
          paginationPerPage={5}
          paginationRowsPerPageOptions={[5, 10, 15, 20]}
        />
      ) : (
        <div className="row justify-content-start">
          <div className="col-12">
            <p>No results found.</p>
          </div>
        </div>
      )}
    </div>
  );
}
